<script setup lang="ts">
import { computed, nextTick, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import type { PaginationQueryParams } from '@/@vxapp/@Enums/PaginationField'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { useCustomersStore } from '@/@vxapp/Customers/useCustomersStore'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import CustomerDialog from '@/components/Dialogs/Customers/CustomerDialog.vue'
import CustomerListToolbar from '@/components/ServiceLists/Customers/CustomerListToolbar.vue'
import CustomersDataTable from '@/components/Tables/Customers/CustomersDataTable.vue'
import { DialogDisplayType } from '@/enums/dialog-display-type'

type CustomerData = Record<string, unknown>
type DialogDisplayTypeValue = (typeof DialogDisplayType)[keyof typeof DialogDisplayType]
type ManagedQueryKey = 'page' | 'limit' | 'sort' | 'search'

definePage({ meta: PageMeta.Customers.List.Meta })
setPageTitle(PageMeta.Customers.List.Title)

const log = createLogger('CustomersListPage')
const route = useRoute()
const router = useRouter()
const customerStore = useCustomersStore() as any

const customersDataTable =
  useTemplateRef<InstanceType<typeof CustomersDataTable>>('customersDataTable')

const customerData = ref<CustomerData | null>(null)
const dialogDisplayType = shallowRef<DialogDisplayTypeValue>(DialogDisplayType.CREATE)
const isCustomerDialogVisible = shallowRef(false)
const isRouteQueryReady = shallowRef(false)
const resultCount = shallowRef<number | null>(null)
const defaultQueryParams: PaginationQueryParams = { ...PaginationField.QueryParams, sort: '+id' }
const managedQueryKeys: ManagedQueryKey[] = ['page', 'limit', 'sort', 'search']
const sortableFields = new Set(['id', 'displayName', 'revenueStreamGUID'])

const queryParams = computed<PaginationQueryParams>(() =>
  normalizeQueryParams(customerStore.queryParams ?? defaultQueryParams)
)

const normalizeQueryValue = (value: unknown): string | undefined => {
  const normalizedValue = Array.isArray(value) ? value[0] : value

  if (typeof normalizedValue === 'number') return String(normalizedValue)

  return typeof normalizedValue === 'string' ? normalizedValue : undefined
}

const normalizePositiveInteger = (value: unknown, fallback: number) => {
  const normalizedValue = normalizeQueryValue(value)
  const parsedValue = normalizedValue ? Number.parseInt(normalizedValue, 10) : Number.NaN

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback
}

const normalizeSort = (value: unknown) => {
  const sort = normalizeQueryValue(value)
  const sortField = sort?.replace(/^[+-]/, '')

  return sort && sortField && sortableFields.has(sortField) && /^[+-]/.test(sort)
    ? sort
    : defaultQueryParams.sort
}

const normalizeQueryParams = (source: Record<string, unknown>): PaginationQueryParams => {
  const parsedLimit = Number.parseInt(normalizeQueryValue(source.limit) ?? '', 10)
  const search = normalizeQueryValue(source.search)?.trim()

  return {
    ...defaultQueryParams,
    page: normalizePositiveInteger(source.page, defaultQueryParams.page),
    limit:
      parsedLimit === -1
        ? parsedLimit
        : normalizePositiveInteger(source.limit, defaultQueryParams.limit),
    sort: normalizeSort(source.sort),
    search: search || undefined,
  }
}

const queryParamsMatch = (left: PaginationQueryParams, right: PaginationQueryParams) =>
  left.page === right.page &&
  left.limit === right.limit &&
  left.sort === right.sort &&
  left.search === right.search

const applyRouteQuery = () => {
  const currentQuery = normalizeQueryParams(customerStore.queryParams ?? defaultQueryParams)
  const routeQuery = normalizeQueryParams(route.query as Record<string, unknown>)

  if (queryParamsMatch(currentQuery, routeQuery)) return false

  customerStore.queryParams = { ...customerStore.queryParams, ...routeQuery }

  return true
}

const buildRouteQuery = (
  source: PaginationQueryParams = normalizeQueryParams(
    customerStore.queryParams ?? defaultQueryParams
  )
) => {
  const nextQuery = { ...route.query } as Record<string, unknown>

  for (const key of managedQueryKeys) delete nextQuery[key]

  const queryParams = normalizeQueryParams(source as unknown as Record<string, unknown>)

  return {
    ...nextQuery,
    ...(queryParams.page !== defaultQueryParams.page ? { page: String(queryParams.page) } : {}),
    ...(queryParams.limit !== defaultQueryParams.limit ? { limit: String(queryParams.limit) } : {}),
    ...(queryParams.sort !== defaultQueryParams.sort ? { sort: queryParams.sort } : {}),
    ...(queryParams.search ? { search: queryParams.search } : {}),
  }
}

const fetchCustomers = async () => {
  await customersDataTable.value?.fetchCustomers()
}

const updateQueryParams = async (patch: Partial<PaginationQueryParams>) => {
  const nextQueryParams = normalizeQueryParams({ ...customerStore.queryParams, ...patch })

  customerStore.queryParams = nextQueryParams

  const nextRouteQuery = buildRouteQuery({ ...nextQueryParams })

  if (JSON.stringify(route.query) !== JSON.stringify(nextRouteQuery))
    await router.replace({ query: nextRouteQuery })

  await nextTick()
  await fetchCustomers()
}

const handleSearch = async (search: string) => {
  await updateQueryParams({ search: search || undefined, page: 1 })
}

const handleClear = async () => {
  customerStore.queryParams = { ...defaultQueryParams }

  const nextRouteQuery = buildRouteQuery(defaultQueryParams)

  if (JSON.stringify(route.query) !== JSON.stringify(nextRouteQuery))
    await router.replace({ query: nextRouteQuery })

  await nextTick()
  await fetchCustomers()
}

const handleSortChange = async (sort: string) => {
  await updateQueryParams({ sort, page: 1 })
}

const handleLimitChange = async (limit: number) => {
  await updateQueryParams({ limit, page: 1 })
}

const handlePageChange = async (page: number) => {
  await updateQueryParams({ page })
}

const handleLoaded = (entriesFound: number) => {
  resultCount.value = entriesFound
}

async function handleCreateCustomer() {
  try {
    const { responseCode } = await customerStore.fetchTemplate({ populateDefaults: true })

    if (responseCode !== ResponseCode.OK) {
      log.error('Failed to load customer template', { responseCode })

      return
    }

    customerData.value = cloneReactiveData(customerStore.template ?? {})
    dialogDisplayType.value = DialogDisplayType.CREATE
    isCustomerDialogVisible.value = true
  } catch (error) {
    log.error('Failed to load customer template', error)
  }
}

const handleEditCustomer = async (clientGUID: string) => {
  try {
    const { responseCode } = await customerStore.fetch(clientGUID)

    if (responseCode !== ResponseCode.OK) {
      log.error('Failed to load customer', { clientGUID, responseCode })

      return
    }

    customerData.value = cloneReactiveData(customerStore.customer ?? {})
    dialogDisplayType.value = DialogDisplayType.EDIT
    isCustomerDialogVisible.value = true
  } catch (error) {
    log.error('Failed to load customer', error)
  }
}

const handleSaveCustomer = async (newCustomerData: CustomerData) => {
  try {
    const response =
      dialogDisplayType.value === DialogDisplayType.CREATE
        ? await customerStore.create(newCustomerData, false)
        : await customerStore.update(newCustomerData, false)

    const expectedCode =
      dialogDisplayType.value === DialogDisplayType.CREATE ? ResponseCode.CREATED : ResponseCode.OK

    if (response.responseCode !== expectedCode) {
      log.error('Failed to save customer', { responseCode: response.responseCode })

      return
    }

    await fetchCustomers()
  } catch (error) {
    log.error('Failed to save customer', error)
  }
}

customerStore.queryParams = { ...defaultQueryParams }
applyRouteQuery()
isRouteQueryReady.value = true

onMounted(fetchCustomers)

watch(
  () => route.query,
  async () => {
    if (!isRouteQueryReady.value || !applyRouteQuery()) return

    await nextTick()
    await fetchCustomers()
  },
  { deep: true }
)
</script>

<template>
  <section
    class="customers-workspace"
    data-testid="customers-workspace"
  >
    <VCard class="customers-workspace__hero">
      <VCardItem>
        <div class="customers-workspace__hero-content">
          <div>
            <span class="customers-workspace__eyebrow">Relationship operations</span>
            <VCardTitle class="customers-workspace__title">Customers</VCardTitle>
            <VCardSubtitle class="customers-workspace__subtitle">
              Find customer accounts, understand their commercial stream, and open the full record.
            </VCardSubtitle>
          </div>
          <div class="customers-workspace__metric">
            <span>Current view</span>
            <strong>{{ resultCount === null ? '—' : resultCount.toLocaleString() }}</strong>
          </div>
        </div>
      </VCardItem>

      <VDivider />

      <VCardActions class="customers-workspace__actions">
        <VBtn
          color="primary"
          data-testid="new-customer-action"
          prepend-icon="tabler-plus"
          @click="handleCreateCustomer"
        >
          New customer
        </VBtn>
        <VBtn
          data-testid="hero-refresh-customers"
          prepend-icon="tabler-refresh"
          variant="tonal"
          @click="fetchCustomers"
        >
          Refresh
        </VBtn>
      </VCardActions>
    </VCard>

    <CustomerListToolbar
      :query-params="queryParams"
      :result-count="resultCount"
      :is-refreshing="customerStore.isLoading"
      @search="handleSearch"
      @sort-change="handleSortChange"
      @limit-change="handleLimitChange"
      @clear="handleClear"
      @refresh="fetchCustomers"
    />

    <CustomersDataTable
      ref="customersDataTable"
      @edit="handleEditCustomer"
      @loaded="handleLoaded"
      @page-change="handlePageChange"
    />
  </section>

  <CustomerDialog
    v-if="customerData"
    v-model:is-dialog-visible="isCustomerDialogVisible"
    :customer-data="customerData"
    :display-type="dialogDisplayType"
    @create="handleSaveCustomer"
    @update="handleSaveCustomer"
  />
</template>

<style scoped lang="scss">
.customers-workspace {
  display: grid;
  gap: 1.5rem;
  font-feature-settings: 'cv01', 'ss03';
}

.customers-workspace__hero {
  overflow: hidden;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background:
    radial-gradient(circle at 88% -30%, rgba(var(--v-theme-primary), 0.2), transparent 28rem),
    linear-gradient(110deg, rgba(var(--v-theme-surface-bright), 0.98), rgb(var(--v-theme-surface)));
  box-shadow: 0 18px 52px rgba(var(--v-theme-on-background), 0.06);
}

.customers-workspace__hero-content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  padding-block: 0.4rem;
}

.customers-workspace__eyebrow {
  color: rgb(var(--v-theme-primary));
  font-size: 0.6875rem;
  font-weight: 590;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.customers-workspace__title {
  margin-block-start: 0.35rem;
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  font-weight: 590;
  letter-spacing: -0.04em;
}

.customers-workspace__subtitle {
  max-inline-size: 48rem;
  margin-block-start: 0.65rem;
  white-space: normal;
}

.customers-workspace__metric {
  display: grid;
  min-inline-size: 7rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.7);
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
}

.customers-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  text-transform: uppercase;
}

.customers-workspace__metric strong {
  font-size: 1.25rem;
  font-weight: 590;
}

.customers-workspace__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
}

@media (max-width: 599px) {
  .customers-workspace {
    gap: 1rem;
  }

  .customers-workspace__hero-content {
    align-items: stretch;
    flex-direction: column;
    gap: 1rem;
  }

  .customers-workspace__metric {
    min-inline-size: 0;
  }

  .customers-workspace__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .customers-workspace__actions :deep(.v-btn) {
    min-block-size: 3rem;
    inline-size: 100%;
  }
}
</style>
